// Imprest retirement UI: the employee's own imprests, Finance's monthly
// retirement queue, and the reimbursement SLA clock.
// Design: docs/expenses/03-imprest-retirement-and-reimbursement-sla.md §7.
//
// TWO AMOUNTS, ALWAYS BOTH. Every row shows the balance FROZEN on the cutoff
// date beside the balance NOW. Showing one without the other is how a notice and
// a screen come to disagree with nobody able to say which is right — an employee
// who cleared on the 27th must still be able to see what the 25th notice said.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Badge } from '@/features/shared/components/ui/badge';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  myImprests, listImprestCycles, getImprestCycle, snapshotImprestCycle,
  excludeImprestItem, publishImprestCycle, reimbursementSLA,
  type ImprestCycle, type ImprestCycleItem, type ReimbursementSLAClaim,
} from '@/lib/expensesApi';

const money = (amount: string, currency: string) =>
  `${currency} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

/** Snapshot beside live, with the difference called out when they differ. */
const BalancePair: React.FC<{ item: ImprestCycleItem; cutoff: string }> = ({ item, cutoff }) => {
  const { t } = useTranslation('expenses');
  const changed = item.snapshotOutstanding !== item.liveOutstanding;
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
      <span className="text-sm">
        <span className="text-muted-foreground">{t('imprest.on_date', { defaultValue: 'On {{date}}:', date: cutoff })}</span>{' '}
        <span className="font-medium tabular-nums">{money(item.snapshotOutstanding, item.currency)}</span>
      </span>
      <span className="text-sm">
        <span className="text-muted-foreground">{t('imprest.now', { defaultValue: 'Now:' })}</span>{' '}
        <span className="font-medium tabular-nums">{money(item.liveOutstanding, item.currency)}</span>
      </span>
      {changed && (
        <span className="text-xs text-success-strong">{t('imprest.cleared_since', { defaultValue: 'cleared since the snapshot' })}</span>
      )}
    </div>
  );
};

/** Not-yet-due items are shown, but visibly set apart — they are never chased. */
const DueChip: React.FC<{ item: ImprestCycleItem }> = ({ item }) => {
  const { t } = useTranslation('expenses');
  if (item.notYetDue) {
    return <Badge className="border-0 bg-slate-100 text-slate-600">{t('imprest.not_yet_due', { defaultValue: 'Not yet due' })}</Badge>;
  }
  if (item.daysOverdue > 0) {
    return <Badge className="border-0 bg-danger-soft text-danger-strong">{t('imprest.days_overdue', { defaultValue: '{{n}}d overdue', n: item.daysOverdue })}</Badge>;
  }
  return <Badge className="border-0 bg-warning-soft text-warning-strong">{t('imprest.due', { defaultValue: 'Due' })}</Badge>;
};

// --- Employee: My Imprests ----------------------------------------------------

export const MyImprestsPanel: React.FC = () => {
  const { t } = useTranslation('expenses');
  const [cycles, setCycles] = useState<{ cycle: ImprestCycle; items: ImprestCycleItem[] }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    myImprests().then(setCycles).catch(() => setCycles([])).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader2 className="h-4 w-4 animate-spin" />;
  if (!cycles.length) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">{t('imprest.my_title', { defaultValue: 'My imprests' })}</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {t('imprest.my_none', { defaultValue: 'You have no outstanding imprests. Nothing to retire.' })}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">My imprests</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        {cycles.map(({ cycle, items }) => (
          <div key={cycle.id} className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span>{cycle.cycleKey}</span>
              {/* The deadline an employee must act on. An extension is shown as
                  an exception with its reason, never as a silently different
                  standard. */}
              {(items ?? []).some((i) => i.extendedDue) && (
                <Badge className="border-0 bg-warning-soft text-warning-strong">{t('imprest.deadline_extended', { defaultValue: 'Deadline extended' })}</Badge>
              )}
            </div>
            {(items ?? []).map((it) => (
              <div key={it.id} className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium">{it.purpose || t('imprest.untitled', { defaultValue: 'Imprest' })}</span>
                  <DueChip item={it} />
                </div>
                <BalancePair item={it} cutoff={cycle.cutoffDate?.slice(0, 10) ?? ''} />
                {it.extendedDue && (
                  <p className="text-xs text-warning-strong">
                    {t('imprest.extended_to', { defaultValue: 'Deadline extended to {{date}}', date: it.extendedDue.slice(0, 10) })}
                    {it.exceptionReason ? ` — ${it.exceptionReason}` : ''}
                  </p>
                )}
              </div>
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// --- Finance: retirement queue ------------------------------------------------

export const ImprestRetirementQueue: React.FC = () => {
  const { t } = useTranslation('expenses');
  const [cycles, setCycles] = useState<ImprestCycle[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ cycle: ImprestCycle; items: ImprestCycleItem[] } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listImprestCycles().then(setCycles).catch(() => setCycles([]));
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    if (!openId) { setDetail(null); return; }
    getImprestCycle(openId).then(setDetail).catch(() => setDetail(null));
  }, [openId]);

  const snapshot = async () => {
    setBusy(true);
    try {
      const r = await snapshotImprestCycle();
      toast.success(r.replayed
        ? t('imprest.toast_replayed', { defaultValue: 'This period was already captured — showing the existing snapshot.' })
        : t('imprest.toast_snapshot', { defaultValue: 'Snapshot taken: {{n}} imprest(s). Nobody has been notified yet.', n: r.cycle.itemCount }));
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('imprest.err_snapshot', { defaultValue: 'Could not take the snapshot.' })), { duration: 6000 });
    } finally { setBusy(false); }
  };

  const publish = async (id: string) => {
    setBusy(true);
    try {
      const r = await publishImprestCycle(id);
      // Be explicit about what publishing did and did NOT do. "Published" must
      // never be read as "everyone has been emailed" when sending is off.
      toast.success(
        t('imprest.toast_published', { defaultValue: 'Published. Deadline {{due}}.', due: r.dueBy }) +
        (r.deadlineExceptions ? ' ' + t('imprest.toast_exceptions', { defaultValue: '{{n}} deadline exception(s) recorded.', n: r.deadlineExceptions }) : '') +
        (r.sendingEnabled ? '' : ' ' + t('imprest.toast_sending_off', { defaultValue: 'Notices recorded — sending is OFF for this workspace.' })),
        { duration: 9000 },
      );
      if (r.deadlineNeedsReview) {
        // react-hot-toast has no .warning; a warning must not read as a success.
        toast(t('imprest.toast_needs_review', { defaultValue: 'Deadline needs review — {{note}}', note: r.deadlineNote }), { duration: 12000, icon: '⚠️' });
      }
      load(); setOpenId(null);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('imprest.err_publish', { defaultValue: 'Could not publish the cycle.' })), { duration: 6000 });
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{t('imprest.queue_title', { defaultValue: 'Imprest retirement' })}</CardTitle>
        <Button size="sm" onClick={snapshot} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('imprest.take_snapshot', { defaultValue: 'Take snapshot' })}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          {t('imprest.queue_hint', { defaultValue: 'A snapshot freezes what is outstanding today. It notifies nobody — review it, then publish to record the private notices.' })}
        </p>
        {!cycles.length && <p className="text-sm text-muted-foreground">{t('imprest.no_cycles', { defaultValue: 'No cycles yet.' })}</p>}
        {cycles.map((c) => (
          <div key={c.id} className="rounded-lg border border-border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{c.cycleKey}</span>
                <Badge className="border-0 bg-slate-100 text-slate-600">{c.status}</Badge>
                <span className="text-xs text-muted-foreground">
                  {t('imprest.counts', { defaultValue: '{{items}} imprest(s) · {{people}} employee(s)', items: c.itemCount, people: c.employeeCount })}
                </span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setOpenId(openId === c.id ? null : c.id)}>
                  {openId === c.id ? t('imprest.hide', { defaultValue: 'Hide' }) : t('imprest.review', { defaultValue: 'Review' })}
                </Button>
                {c.status !== 'PUBLISHED' && c.status !== 'CLOSED' && (
                  <Button size="sm" disabled={busy} onClick={() => publish(c.id)}>{t('imprest.publish', { defaultValue: 'Publish' })}</Button>
                )}
              </div>
            </div>
            {openId === c.id && detail && (
              <div className="mt-3 space-y-2">
                {!(detail.items ?? []).length && <p className="text-sm text-muted-foreground">{t('imprest.nothing_outstanding', { defaultValue: 'Nothing outstanding.' })}</p>}
                {(detail.items ?? []).map((it) => (
                  <div key={it.id} className="rounded border border-border p-2 space-y-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm">{it.purpose || t('imprest.untitled', { defaultValue: 'Imprest' })}</span>
                      <div className="flex items-center gap-2">
                        <DueChip item={it} />
                        <Badge className="border-0 bg-slate-100 text-slate-600">{it.state}</Badge>
                      </div>
                    </div>
                    <BalancePair item={it} cutoff={detail.cycle.cutoffDate?.slice(0, 10) ?? ''} />
                    {it.state === 'OUTSTANDING' && detail.cycle.status !== 'PUBLISHED' && (
                      <ExcludeControl cycleId={detail.cycle.id} itemId={it.id}
                        onDone={() => getImprestCycle(detail.cycle.id).then(setDetail)} />
                    )}
                    {it.excludedReason && (
                      <p className="text-xs text-muted-foreground">{t('imprest.excluded_reason', { defaultValue: 'Excluded — {{reason}}', reason: it.excludedReason })}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

/** An exclusion must say why: it is a decision recorded on the snapshot, not a
 *  correction of it, so the reason is required rather than optional. */
const ExcludeControl: React.FC<{ cycleId: string; itemId: string; onDone: () => void }> = ({ cycleId, itemId, onDone }) => {
  const { t } = useTranslation('expenses');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) {
    return <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>{t('imprest.exclude_open', { defaultValue: 'Exclude…' })}</Button>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      <input
        className="h-8 flex-1 min-w-[200px] rounded-md border border-border bg-background px-2 text-sm"
        placeholder={t('imprest.exclude_why', { defaultValue: 'Why is this excluded? (required)' })}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <Button size="sm" disabled={busy || !reason.trim()} onClick={async () => {
        setBusy(true);
        try {
          await excludeImprestItem(cycleId, itemId, reason.trim());
          setOpen(false); setReason(''); onDone();
        } catch (e) {
          toast.error(getApiErrorMessage(e, t('imprest.err_exclude', { defaultValue: 'Could not exclude this item.' })), { duration: 6000 });
        } finally { setBusy(false); }
      }}>{t('imprest.exclude', { defaultValue: 'Exclude' })}</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>{t('imprest.cancel', { defaultValue: 'Cancel' })}</Button>
    </div>
  );
};

// --- Finance: reimbursement SLA ----------------------------------------------

export const ReimbursementSLAPanel: React.FC = () => {
  const { t } = useTranslation('expenses');
  const [data, setData] = useState<{ slaDays: number; claims: ReimbursementSLAClaim[] } | null>(null);
  useEffect(() => { reimbursementSLA().then(setData).catch(() => setData(null)); }, []);
  if (!data) return null;
  // Defensive even though the API now returns []: a list that arrives as null
  // must degrade to "nothing to show", never take the page down. This exact
  // crash reached dev — the server sent null for an empty result.
  const claims = data.claims ?? [];
  const breached = claims.filter((c) => c.breached);
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t('imprest.sla_title', { defaultValue: 'Reimbursement SLA ({{n}} days)', n: data.slaDays })}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          {t('imprest.sla_hint_long', { defaultValue: 'Measured from final approval. A failed payment does not extend the deadline — it is reported as a breach with the time taken to recover.' })}
        </p>
        {!claims.length && <p className="text-sm text-muted-foreground">{t('imprest.no_claims', { defaultValue: 'No approved claims.' })}</p>}
        {breached.length > 0 && (
          <p className="text-sm font-medium text-danger-strong">{t('imprest.past_due', { defaultValue: '{{n}} past due', n: breached.length })}</p>
        )}
        {claims.slice(0, 25).map((c) => (
          <div key={c.reportId} className="flex flex-wrap items-center justify-between gap-2 rounded border border-border p-2">
            <span className="text-sm">{c.reportNumber}</span>
            <span className="text-sm tabular-nums">{money(c.amount, c.currency)}</span>
            <span className="text-xs text-muted-foreground">{t('imprest.due_on', { defaultValue: 'due {{date}}', date: c.dueAt?.slice(0, 10) })}</span>
            {c.paidAt
              ? <Badge className={`border-0 ${c.slaMet ? 'bg-success-soft text-success-strong' : 'bg-danger-soft text-danger-strong'}`}>
                  {c.slaMet ? t('imprest.paid_on_time', { defaultValue: 'paid on time' }) : t('imprest.paid_late', { defaultValue: 'paid late' })}
                  {c.recoveryDays != null ? ' · ' + t('imprest.recovered_in', { defaultValue: 'recovered in {{n}}d', n: c.recoveryDays }) : ''}
                </Badge>
              : <Badge className={`border-0 ${c.breached ? 'bg-danger-soft text-danger-strong' : 'bg-warning-soft text-warning-strong'}`}>
                  {c.breached ? t('imprest.overdue', { defaultValue: 'overdue' }) : t('imprest.awaiting_payment', { defaultValue: 'awaiting payment' })}
                </Badge>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
